import { Tag } from "@teammood/shared/ui/tags-input";
import Delta from "quill-delta";
import { TaskAssignee, TaskLabel } from "../../taskbook-triage/taskbook.types";

export interface TimelineItem {
    date?: Date,
    id?: string,
    teamId?: string,
    itemType: TimelineItemType,
    educationalCard?: string,
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;
}

export type Reaction = {
    id: ReactionId,
    count: number,
    selected: boolean
}

export enum ReactionsEnum {
    agree = 'agree',
    disagree = 'disagree',
    care = 'care',
    love = 'love',
    laugh = 'laugh',
    surprise = 'surprise',
    thinking = 'thinking',
    sad = 'sad'
}

export type ReactionId = keyof typeof ReactionsEnum;


export interface Reactions {
    reactions: Reaction[]
}

export class RichTextComment {
    rawTextFormat: string;
    htmlFormat: string;
    objectModelFormat: Delta

    constructor(rawTextFormat: string, htmlFormat: string, objectModelFormat: Delta) {
        this.rawTextFormat = rawTextFormat;
        this.htmlFormat = htmlFormat;
        this.objectModelFormat = objectModelFormat;
    }

    static createNew(): RichTextComment {
        return new RichTextComment("", "", new Delta());
    }

    isEmpty(): boolean {
        return RichTextComment.isEmptyDelta(this.objectModelFormat);
    }

    static isEmptyDelta(delta: Delta): boolean {
        return delta.ops.length === 0 ||
            delta.ops.filter(op => {
                return op.insert.toString().trim() !== '';
            }).length === 0;
    }
}


export class Thread implements IsEditable {
    id?: string;
    author: ThreadAuthor;
    message: RichTextComment;
    datetime: string;
    isEdited: boolean;
    isMoodAuthor: boolean;
    moderationStatus?: string;
    linkPreviews?: LinkPreview[];
    isAnonymous: boolean;
    isEditable: boolean;
    reactions: Reaction[];

    constructor(isAnonymous: boolean = true) {
        this.message = RichTextComment.createNew();
        this.isAnonymous = isAnonymous;
        this.id =  (Math.random() + 1).toString(36).substring(7);
    }
}

export interface IsDeletable {
    isDeletable: boolean
}

export interface IsEditable {
    isEditable: boolean
}

export interface Threads {
    threads: Thread[],
    threadAuthors: ThreadAuthor[]
}

export interface ThreadAuthor {
    type: 'NonAnonymousAuthor' | 'AnonymousAuthor';
    name: string;
    firstname?: string;
    lastname?: string;
    color?: string;
    icon?: string;
    id?: string;
}

export interface EducationalCard {
    educationalCard?: string;
}

export type MoodValue = 'excellent' | 'good'  | 'average'  | 'hard'  | 'bad';

export type TimelineItemType = 'moodItem' | 'kudosItem' | 'topicItem' | 'badMoodStreakItem' | 'badMoodPassItem' | 'tipsItem' | TimelinePollResultTimelineItemType;

export type TimelinePollResultTimelineItemType = 'closedQuestionResultItem' | 'agreeDisagreeQuestionResultItem' | 'scaleQuestionResultItem' | 'fiveStarsQuestionResultItem';


export class Mood implements TimelineItem, Reactions, Threads, IsDeletable, IsEditable, EducationalCard {
    date: Date;
    id: string;
    teamId: string;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "moodItem";
    threadAuthors: ThreadAuthor[];
    mood: MoodValue;
    comment: {
        body: string;
        linkPreviews: LinkPreview[];
    };
    isDeletable: boolean;
    isEditable: boolean;
    tags: Tag[];
    educationalCard?: string;
    commentSaved: boolean = true;
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;
}

export interface ReviewInfo {
    name: string,
    date: string
}

export interface Author {
    id: string,
    fullname: string,
}

export interface KudosRecipient  {
    id: string,
    fullname: string,
}

export type KudosType = 'thanks' | 'congratulate' | 'celebrate'
export class KudosRecipients {
    allTeamSelected: boolean;
    selectedTeammates: KudosRecipient[];

    constructor(){
        this.allTeamSelected = false;
        this. selectedTeammates = [];
    }
}

export class Kudos implements TimelineItem, Reactions, Threads, IsDeletable, IsEditable, EducationalCard {
    date: Date;
    id: string;
    teamId?: string;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "kudosItem";
    message:  {
        body: string;
        linkPreviews: LinkPreview[];
    };
    recipients: KudosRecipients;
    kudosType?: KudosType;
    author?: Author;
    threadAuthors: ThreadAuthor[];
    isDeletable: boolean;
    isEditable: boolean;
    educationalCard?: string;
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;
    privacy: 'public' | 'private' = 'public';

    constructor(author?: Author) {
        this.date = new Date();
        this.reactions = [];
        this.threads = [];
        this.threadAuthors = [];
        this.message = {
            body: '',
            linkPreviews: []
        };
        this.recipients = new KudosRecipients();
        this.isDeletable = false;
        this.isEditable = false;
        this.reviewed = null;
        this.managerPanel = null;
        this.author = author;
    }
}



export class GifPreview {
    url: string;
    name: string;
    image: string;

    constructor(linkPreviews: LinkPreview) {
        const imagePathArray = linkPreviews.url.split('/');
        if (imagePathArray.length > 1) {
            this.name = imagePathArray[imagePathArray.length-1];
        } else {
            this.name = '';
        }
        this.url = linkPreviews.url;
        this.image = linkPreviews.image;
    }
}

export interface LinkPreview {
    url: string,
    domain: string,
    title?: string,
    description?: string,
    image?: string
}

export type TopicType = 'topic' | 'issue' | 'question';
export type QuestionType = 'team_spirit' | 'management' | 'remote_work' | 'fun' | 'agile' | 'about_teammood'
export class Topic implements TimelineItem, Reactions, Threads, IsDeletable, IsEditable, EducationalCard {
    date: Date;
    id: string;
    teamId: string;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "topicItem";
    threadAuthors: ThreadAuthor[];
    title: {
        body: string;
        linkPreviews: LinkPreview[];
    };
    type: TopicType;
    isDeletable: boolean;
    isEditable: boolean;
    educationalCard?: string;
    questionType?: QuestionType;
    templateId?: string;
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;

    constructor(type: TopicType, templateId: string) {
        this.date = new Date();
        this.type = type;
        this.title = {
            body: '',
            linkPreviews: []
        }
        this.reactions = [];
        this.threads = [];
        this.threadAuthors = [];
        this.isDeletable = false;
        this.isEditable = false;
        this.templateId = templateId;
        this.reviewed = null;
        this.managerPanel = null;
    }
}

export class BadMoodStreak implements TimelineItem, Reactions, Threads {
    date: Date;
    id: string;
    teamId: string;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "badMoodStreakItem";
    badMoodCount: number;
    threadAuthors: ThreadAuthor[];
    educationalCard?: string;
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;
}

export class BadMoodPass implements TimelineItem, Reactions, Threads {
    date: Date;
    id: string;
    teamId: string;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "badMoodPassItem";
    badMoodCount: number;
    threadAuthors: ThreadAuthor[];
    educationalCard?: string;
    reviewed: ReviewInfo[];
    managerPanel?: ManagerPanel;
}

export class Tip implements TimelineItem {
    id: string;
    date: Date;
    teamId: string;
    itemType: TimelineItemType = "tipsItem";
    title: string;
    description: string;
    htmlBody: string;
    show: boolean;
    reviewed: ReviewInfo[];
}

export interface ManagerPanel {
    assignees: TaskAssignee[];
    labels: TaskLabel[];
    backlogLink: string;
}

